import { ScrollView, StyleSheet, View } from 'react-native'
import { useLocalSearchParams, Link } from 'expo-router'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '../../../../../app/design-system'
import { SectionTitle } from '../../../../../app/components/Admin/SectionTitle'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Text } from 'react-native'

type LeaderboardRow = {
  _id: string
  userId: string
  handsPlayed: number
  sessions: number
  totalPlayTimeMs: number
  lastActiveAt: number
}

const columnHelper = createColumnHelper<LeaderboardRow>()

const columns = [
  columnHelper.accessor('userId', {
    header: 'User ID',
    cell: (info) => <DS.Text selectable variant="Body Small/Regular">{info.getValue()}</DS.Text>
  }),
  columnHelper.accessor('handsPlayed', {
    header: 'Hands Played',
    cell: (info) => <DS.Text variant="Body Small/Medium">{info.getValue().toLocaleString()}</DS.Text>
  }),
  columnHelper.accessor('sessions', {
    header: 'Sessions',
    cell: (info) => <DS.Text variant="Body Small/Regular">{info.getValue()}</DS.Text>
  }),
  columnHelper.accessor('totalPlayTimeMs', {
    header: 'Play Time',
    cell: (info) => {
      const mins = Math.round(info.getValue() / 60000)
      return <DS.Text variant="Body Small/Regular">{mins} min</DS.Text>
    }
  }),
  columnHelper.accessor('lastActiveAt', {
    header: 'Last Active',
    cell: (info) => (
      <DS.Text variant="Body Small/Regular">{new Date(info.getValue()).toLocaleDateString()}</DS.Text>
    )
  })
]

export default function LoungeReport() {
  const { loungeId } = useLocalSearchParams<{ loungeId: string }>()

  const leaderboard = useQuery(
    api.lounges.queries.getLoungeLeaderboard,
    loungeId ? { loungeId: loungeId as Id<'lounges'> } : 'skip'
  )

  const table = useReactTable({
    data: (leaderboard ?? []) as LeaderboardRow[],
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <ScrollView>
      <View style={styles.container}>
        <SectionTitle title="Lounge Report" subtitle="Player stats summary for this lounge" />

        {/* CSV export link */}
        <View style={styles.exportRow}>
          <DS.Text variant="Body Large/Regular">
            Export:{' '}
            <DS.Text
              variant="Body Large/Medium"
              style={{ color: DS.palette['Brand/Maroon'] }}
              onPress={() => {
                if (typeof window !== 'undefined') {
                  window.open(`/api/lounges/${loungeId}/report.csv`, '_blank')
                }
              }}
            >
              Download CSV
            </DS.Text>
          </DS.Text>
        </View>

        {/* Leaderboard table */}
        <View style={styles.tableContainer}>
          <View style={styles.tableHeader}>
            {table.getHeaderGroups().map((hg) => (
              <View style={styles.tableRow} key={hg.id}>
                {hg.headers.map((h) => (
                  <View style={styles.tableHeaderCell} key={h.id}>
                    <Text style={styles.headerText}>
                      {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
          <View>
            {table.getRowModel().rows.map((row) => (
              <View style={styles.tableRow} key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <View style={styles.tableCell} key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </View>
                ))}
              </View>
            ))}
            {(leaderboard ?? []).length === 0 && (
              <View style={{ padding: 24, alignItems: 'center' }}>
                <DS.Text variant="Body Small/Regular" style={{ color: '#6b7280' }}>
                  No data yet.
                </DS.Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 24,
    gap: 24
  },
  exportRow: {
    padding: 16,
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e9ecef'
  },
  tableContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e9ecef'
  },
  tableHeader: {
    backgroundColor: '#eeedea'
  },
  tableRow: {
    flexDirection: 'row'
  },
  tableHeaderCell: {
    flex: 1,
    padding: 12,
    borderBottomWidth: 2,
    borderBottomColor: '#dee2e6'
  },
  headerText: {
    fontWeight: '600',
    color: '#6b7280'
  },
  tableCell: {
    flex: 1,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6'
  }
})
